import { createContext, useContext, useState, useEffect, useRef } from 'react'

/**
 * PromoContext - Manages promo codes and partner information across the application
 * 
 * IMPORTANT: Each API request should only be called once to avoid duplicate calls:
 * - GET /api/promo/contractor is called only on component mount and when the promo changes
 * - POST /api/promo/clear is called only when user explicitly clears promo
 * 
 * The promo code is passed directly to /api/promo/contractor via query string.
 */
const PromoContext = createContext()
const CLAIMFORM_STORAGE_KEY = 'claimform_state'

function getCookie(name) {
  const value = document.cookie.split('; ').find((cookie) => cookie.startsWith(`${name}=`))
  return value ? decodeURIComponent(value.slice(name.length + 1)) : null
}

function readPersistedPromo() {
  try {
    const claimformState = JSON.parse(localStorage.getItem(CLAIMFORM_STORAGE_KEY) || '{}')
    return {
      promo: getCookie('promo') || claimformState.promo || null,
      partnerLogo: claimformState.partnerLogo || null,
      partnerWebsite: claimformState.partnerWebsite || null,
      contractor: claimformState.contractor || null,
    }
  } catch {
    return { promo: null, partnerLogo: null, partnerWebsite: null, contractor: null }
  }
}

export function PromoProvider({ children }) {
  const persisted = readPersistedPromo()
  const [promo, setPromo] = useState(persisted.promo)
  const [partnerLogo, setPartnerLogo] = useState(persisted.partnerLogo)
  const [partnerWebsite, setPartnerWebsite] = useState(persisted.partnerWebsite)
  const [contractor, setContractor] = useState(persisted.contractor)
  const [loading, setLoading] = useState(true)
  const hasLoaded = useRef(false)
  const [previousPromo, setPreviousPromo] = useState(persisted.promo)

  // Function to update promo in claimform localStorage object
  const updateClaimformPromo = (promoCode) => {
    try {
        const claimformState = JSON.parse(localStorage.getItem(CLAIMFORM_STORAGE_KEY) || '{}')
        claimformState.promo = promoCode
        localStorage.setItem(CLAIMFORM_STORAGE_KEY, JSON.stringify(claimformState))
    } catch (error) {
        console.error('Failed to update claimform_state promo:', error)
    }
  }

  // Function to load promo info
  const loadPromoInfo = async (search, forceApiCall = false) => {
    const urlParams = new URLSearchParams(search)
    const incomingPromo = urlParams.get('promo')
    // If URL has no promo param, fall back to the stored promo so contractor
    // data (logo / website) can be restored even without a promo in the URL.
    const promoToFetch = incomingPromo === null ? previousPromo : (incomingPromo || null)

    // If the promo didn't change and we already have contractor data, skip the API call.
    if (promoToFetch === previousPromo && contractor && hasLoaded.current && !forceApiCall) {
      setLoading(false)
      return
    }

    // Only call API if promo changed or forceApiCall is true
    if ((promoToFetch !== previousPromo || forceApiCall) && hasLoaded.current) {
      try {
        // Load contractor data directly, passing promo in the query string
        const contractorUrl = promoToFetch
          ? `/api/promo/contractor?promo=${encodeURIComponent(promoToFetch)}`
          : '/api/promo/contractor'
        const response = await fetch(contractorUrl)
        if (response.ok) {
          const text = await response.text()
          const data = text ? JSON.parse(text) : null
          if (data) {
            const contractor = {
              enableExpress: data.EnableExpress,
              isTourOperator: data.IsTourOperator,
              basicCommissionType: data.BasicCommissionType,
              basicCommissionValue: data.BasicCommissionValue,
              basicCommissionCrmId: data.BasicCommissionCrmID,
              expressCommissionType: data.ExpressCommissionType,
              expressCommissionValue: data.ExpressCommissionValue,
              expressCommissionCrmId: data.ExpressCommissionCrmID,
              logoBase64: data.LogoBase64,
              website: data.Website,
            }
            setPromo(promoToFetch)
            setPartnerLogo(data.LogoBase64 || null)
            setPartnerWebsite(data.Website || null)
            setContractor(contractor)
            setPreviousPromo(promoToFetch)
            updateClaimformPromo(promoToFetch)
          } else {
            // Promo cleared or not found
            setPromo(promoToFetch)
            setPartnerLogo(null)
            setPartnerWebsite(null)
            setContractor(null)
            setPreviousPromo(promoToFetch)
            updateClaimformPromo(promoToFetch)
          }
        } else {
          // API error: keep the current promo/partner info so we don't
          // clear persisted data on a temporary server error.
          setPreviousPromo(promoToFetch)
          updateClaimformPromo(promoToFetch)
        }
      } catch (error) {
        console.error('Failed to load promo info:', error)
        // Network error: keep the current promo/partner info
      } finally {
        setLoading(false)
      }
    } else {
      // Promo didn't change, just update loading state
      setLoading(false)
    }
  }

  // Load on mount (force API call to get initial data)
  useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true
      loadPromoInfo(window.location.search, true)
    }
  }, [])

  // Listen for URL changes (for SPA navigation)
  useEffect(() => {
    const handleUrlChange = () => {
      loadPromoInfo(window.location.search, false)
    }

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleUrlChange)
    
    // Custom event for pushState/replaceState (React Router)
    const originalPushState = history.pushState
    const originalReplaceState = history.replaceState
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args)
      setTimeout(handleUrlChange, 0)
    }
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args)
      setTimeout(handleUrlChange, 0)
    }

    return () => {
      window.removeEventListener('popstate', handleUrlChange)
      history.pushState = originalPushState
      history.replaceState = originalReplaceState
    }
  }, [])

  const applyPromo = async (promoCode) => {
    setLoading(true)
    const codeToFetch = promoCode || null
    try {
      const contractorUrl = codeToFetch
        ? `/api/promo/contractor?promo=${encodeURIComponent(codeToFetch)}`
        : '/api/promo/contractor'
      const response = await fetch(contractorUrl)
      if (response.ok) {
        const text = await response.text()
        const payload = text ? JSON.parse(text) : null
        if (payload) {
          const mappedContractor = {
            enableExpress: payload.EnableExpress,
            isTourOperator: payload.IsTourOperator,
            basicCommissionType: payload.BasicCommissionType,
            basicCommissionValue: payload.BasicCommissionValue,
            basicCommissionCrmId: payload.BasicCommissionCrmID,
            expressCommissionType: payload.ExpressCommissionType,
            expressCommissionValue: payload.ExpressCommissionValue,
            expressCommissionCrmId: payload.ExpressCommissionCrmID,
            logoBase64: payload.LogoBase64,
            website: payload.Website,
          }
          setPromo(codeToFetch)
          setPartnerLogo(payload.LogoBase64 || null)
          setPartnerWebsite(payload.Website || null)
          setContractor(mappedContractor)
          setPreviousPromo(codeToFetch)
          updateClaimformPromo(codeToFetch)
        } else {
          setPromo(codeToFetch)
          setPartnerLogo(null)
          setPartnerWebsite(null)
          setContractor(null)
          setPreviousPromo(codeToFetch)
          updateClaimformPromo(codeToFetch)
        }
      } else {
        setPreviousPromo(codeToFetch)
        updateClaimformPromo(codeToFetch)
      }
    } catch (error) {
      console.error('Failed to apply promo:', error)
    } finally {
      setLoading(false)
    }
  }

  const clearPromo = async () => {
    try {
      await fetch('/api/promo/clear', { method: 'POST' })
      setPromo(null)
      setPartnerLogo(null)
      setPartnerWebsite(null)
      setContractor(null)
    } catch (error) {
      console.error('Failed to clear promo:', error)
    }
  }

  const value = {
    promo,
    partnerLogo,
    partnerWebsite,
    contractor,
    loading,
    hasPromo: !!promo,
    hasContractor: !!contractor,
    enableExpress: contractor?.enableExpress,
    isTourOperator: contractor?.isTourOperator,
    applyPromo,
    clearPromo
  }

  return (
    <PromoContext.Provider value={value}>
      {children}
    </PromoContext.Provider>
  )
}

export function usePromo() {
  const context = useContext(PromoContext)
  if (!context) {
    throw new Error('usePromo must be used within a PromoProvider')
  }
  return context
}

export default PromoContext
